/**
 * Time Domain Visualizer - Shows waveforms over time
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class TimeVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'time',
    name: 'Time Domain',
    description: 'Display waveforms in the time domain',
    category: 'time',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, activeWaves, speed, time, colorMode = 'wave', mergeChannels = false, uploadedAudioData, isUploadedAudioPlaying } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Clear and draw grid
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    this.drawGrid(ctx, width, height, 'detailed');

    // Center line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, cy);
    ctx.lineTo(width, cy);
    ctx.stroke();

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) return;

    // If merging channels, separate waves by channel and combine them
    let wavesToDraw = displayWaves;
    if (mergeChannels) {
      // Group waves by channel and create merged versions
      const leftWaves = displayWaves.filter(w => w.channel === 'left' || !w.channel);
      const rightWaves = displayWaves.filter(w => w.channel === 'right');
      
      // If we have both left and right, merge them into one combined wave
      if (leftWaves.length > 0 && rightWaves.length > 0) {
        // For merged display, show only non-channel-specific waves and a combined representation
        wavesToDraw = displayWaves.filter(w => !w.channel || (w.channel !== 'left' && w.channel !== 'right'));
        
        // Add a synthetic merged wave that combines all channels
        const mergedWave = {
          ...displayWaves[0],
          channel: undefined,
          color: '#888888' // Gray for merged
        };
        if (wavesToDraw.length === 0) {
          wavesToDraw = [mergedWave];
        }
      }
    }

    // Draw each wave
    wavesToDraw.forEach((wave, idx) => {
      ctx.beginPath();
      // Use helper method to get color based on mode
      ctx.strokeStyle = this.getWaveColor(wave, colorMode);
      ctx.lineWidth = 2.5;
      ctx.lineJoin = 'round';

      const maxPossibleAmp = wavesToDraw.reduce((sum, w) => sum + w.amp, 0);
      const scale = maxPossibleAmp > 120 ? (120 / maxPossibleAmp) : 1;

      for (let x = 0; x < width; x += 1) {
        const t = x / width;
        const phaseRad = (wave.phase * Math.PI) / 180;
        const effectiveFreq = wave.freq * speed;
        const angle = 2 * Math.PI * effectiveFreq * t + phaseRad + (time * 2);
        const yVal = (wave.amp / 100) * (height / 2.5) * this.sampleWaveform(wave.waveform, angle) * scale;

        if (x === 0) ctx.moveTo(x, cy - yVal);
        else ctx.lineTo(x, cy - yVal);
      }
      ctx.stroke();
    });
  }
}
